import React, { useState, useEffect, useRef } from 'react';

const statusMenuStyle = {
  marginTop: 4,
  minWidth: '100%',
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: 8,
  boxShadow: '0 12px 16px -4px rgba(10,13,18,0.08), 0 4px 6px -2px rgba(10,13,18,0.03), 0 2px 2px -1px rgba(10,13,18,0.04)',
  padding: '4px 0',
  backgroundColor: 'var(--sl-bg-primary)',
  zIndex: 1000,
};

const statusMenuItemStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '6px 12px',
  border: 'none',
  background: 'transparent',
  textAlign: 'left',
  cursor: 'pointer',
};

function formatRelativeTime(timestamp, fallback) {
  if (!timestamp) return fallback || '';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return fallback || timestamp;

  const now = new Date();
  const diffMs = now - date;
  if (diffMs < 0) return fallback || timestamp;

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return 'Just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes === 1 ? '' : 's'} ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

  return fallback || timestamp;
}

export const deriveNoteTitle = (body) => body.trim().slice(0, 50);

function CrmNotesCard({
  notes = [],
  loading = false,
  error = '',
  onCreateNote,
  onEditNote,
  onDeleteNote,
  canManageNotes = false,
  showContactName = false,
  readOnly = false,
  attachments = [],
  attachmentsLoading = false,
  attachmentsError = '',
  onUploadAttachment,
  onDeleteAttachment,
  canManageAttachments = false,
}) {
  const [expandedNoteIds, setExpandedNoteIds] = useState([]);
  const [openNoteMenuId, setOpenNoteMenuId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const noteMenuRef = useRef(null);
  const fileInputRef = useRef(null);

  const [noteBody, setNoteBody] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [composerError, setComposerError] = useState('');

  const handleCreateNote = async () => {
    const body = noteBody.trim();
    if (!body || !onCreateNote || noteSubmitting) return;
    setNoteSubmitting(true);
    setComposerError('');
    try {
      await onCreateNote(body);
      setNoteBody('');
    } catch (err) {
      setComposerError('Failed to add note');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const supportsAttachments = Boolean(onUploadAttachment);
  const showAttachments = supportsAttachments && activeTab === 'attachments';

  const handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file && onUploadAttachment) onUploadAttachment(file);
    event.target.value = '';
  };

  useEffect(() => {
    if (!openNoteMenuId) return;

    function handleClickOutside(event) {
      if (noteMenuRef.current && !noteMenuRef.current.contains(event.target)) {
        setOpenNoteMenuId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openNoteMenuId]);

  return (
    <div
      className="card h-100"
      style={{
        borderRadius: 12,
        border: '1px solid var(--sl-border-secondary)',
      }}
    >
      <div className="card-body d-flex flex-column p-0">
        <div
          style={{
            padding: '20px 24px 24px',
            borderBottom: '1px solid var(--sl-border-secondary)',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2
              className="mb-0"
              style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}
            >
              Notes
            </h2>
          </div>

          {/* Tabs: All / Attachments */}
          <div>
            <div
              className="d-inline-flex align-items-center"
              style={{
                backgroundColor: 'var(--sl-bg-secondary)',
                borderRadius: 8,
                border: '1px solid var(--sl-border-secondary)',
                padding: 4,
                columnGap: 4,
              }}
            >
              {[
                { key: 'all', label: 'All', count: notes.length },
                { key: 'attachments', label: 'Attachments', count: attachments.length },
              ].map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    className="btn btn-sm d-inline-flex align-items-center gap-2"
                    onClick={() => setActiveTab(tab.key)}
                    disabled={!supportsAttachments}
                    style={{
                      borderRadius: 6,
                      backgroundColor: isActive ? 'var(--sl-bg-primary)' : 'transparent',
                      border: 'none',
                      boxShadow: isActive
                        ? '0px 1px 3px rgba(10, 13, 18, 0.1), 0px 1px 2px -1px rgba(10, 13, 18, 0.1)'
                        : 'none',
                      fontSize: 14,
                      fontWeight: 600,
                      color: isActive ? 'var(--sl-text-secondary)' : 'var(--sl-text-quaternary)',
                      padding: '8px 12px',
                      height: 36,
                    }}
                  >
                    <span>{tab.label}</span>
                    <span
                      className="d-inline-flex align-items-center justify-content-center"
                      style={{
                        minWidth: 18,
                        height: 18,
                        borderRadius: 999,
                        backgroundColor: 'var(--sl-bg-secondary)',
                        border: '1px solid var(--sl-border-secondary)',
                        fontSize: 12,
                        lineHeight: '18px',
                        color: isActive ? 'var(--sl-text-secondary)' : 'var(--sl-text-quaternary)',
                        padding: '2px 8px',
                      }}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div
          style={{
            overflowY: openNoteMenuId ? 'visible' : 'auto',
            maxHeight: openNoteMenuId ? 'none' : 320,
            padding: '12px 24px 12px',
          }}
        >
          {!showAttachments && (
            <>
          {loading && (
            <div className="d-flex align-items-center justify-content-center text-muted small h-100">
              Loading notes...
            </div>
          )}
          {!loading && error && (
            <div className="text-danger small py-2">
              {error}
            </div>
          )}
          {!loading && !error && notes.length === 0 && (
            <div className="d-flex align-items-center justify-content-center text-muted small py-4">
              No notes yet.
            </div>
          )}
          {!loading && !error && notes.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {notes.map((note, index) => {
                const authorName =
                  (note.team_membership && note.team_membership.name) ||
                  'Unknown';
                const authorInitial = authorName.charAt(0).toUpperCase();
                const rawBody = note.sanitized_body || '';
                const plainBody = rawBody
                  .replace(/<[^>]+>/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim();
                const MAX_PREVIEW_CHARS = 160;
                const isLong = plainBody.length > MAX_PREVIEW_CHARS;
                const isExpanded = expandedNoteIds.includes(note.id);
                const previewText = isLong
                  ? `${plainBody.slice(0, MAX_PREVIEW_CHARS).trim()}…`
                  : plainBody;

                const baseTimestamp =
                  note.created_at || note.human_created_at;
                const relativeCreatedAt = baseTimestamp
                  ? formatRelativeTime(
                      baseTimestamp,
                      note.human_created_at || baseTimestamp,
                    )
                  : '';

                const isLastNote = index === notes.length - 1;

                // For DDP, show contact name if available
                const contactName = showContactName && note.contact_name
                  ? note.contact_name
                  : null;

                return (
                  <div
                    key={note.id}
                    style={{
                      paddingBottom: 16,
                      borderBottom: isLastNote ? 'none' : '1px solid var(--sl-border-secondary)',
                    }}
                  >
                    {/* Header row: avatar, name, timestamp, kebab */}
                    <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: 28,
                          height: 28,
                          fontSize: 12,
                          fontWeight: 600,
                          backgroundColor: 'var(--sl-bg-secondary)',
                          color: 'var(--sl-text-tertiary)',
                          border: '0.75px solid var(--sl-border-secondary)',
                        }}
                      >
                        {authorInitial}
                      </div>

                      <div className="d-flex flex-column">
                        <div
                          className="fw-semibold"
                          style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}
                        >
                          {authorName}
                        </div>
                        {contactName && (
                          <div
                            style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}
                          >
                            on {contactName}
                          </div>
                        )}
                      </div>

                      <div className="flex-grow-1" />

                      <div
                        ref={openNoteMenuId === note.id ? noteMenuRef : null}
                        className="d-flex align-items-center position-relative"
                        style={{ columnGap: 8 }}
                      >
                        {relativeCreatedAt && (
                          <div
                            className="small"
                            style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}
                          >
                            {relativeCreatedAt}
                          </div>
                        )}
                        {canManageNotes && !readOnly && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-muted"
                            aria-label="More note actions"
                            onClick={() =>
                              setOpenNoteMenuId((current) =>
                                current === note.id ? null : note.id,
                              )
                            }
                          >
                            <i className="fal fa-ellipsis-v" aria-hidden="true" />
                          </button>
                        )}

                        {openNoteMenuId === note.id && (
                          <div
                            className="dropdown-menu show"
                            style={{
                              ...statusMenuStyle,
                              position: 'absolute',
                              right: 0,
                              left: 'auto',
                              top: '100%',
                              minWidth: 140,
                              zIndex: 9999,
                            }}
                          >
                            <button
                              type="button"
                              style={statusMenuItemStyle}
                              onClick={() => {
                                setOpenNoteMenuId(null);
                                if (onEditNote) onEditNote(note);
                              }}
                            >
                              <span className="figma-text sm">Edit note</span>
                            </button>
                            <button
                              type="button"
                              style={{
                                ...statusMenuItemStyle,
                                color: 'var(--sl-text-error)',
                              }}
                              onClick={() => {
                                setOpenNoteMenuId(null);
                                if (onDeleteNote) onDeleteNote(note);
                              }}
                            >
                              <span className="figma-text sm">Delete note</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Note body - full width */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {isExpanded && note.sanitized_body ? (
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: 'var(--sl-text-tertiary)',
                            lineHeight: '20px',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: note.sanitized_body,
                          }}
                        />
                      ) : (
                        <p
                          className="mb-0"
                          style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: 'var(--sl-text-tertiary)',
                            lineHeight: '20px',
                          }}
                        >
                          {previewText || 'No content'}
                        </p>
                      )}

                      {isLong && !isExpanded && (
                        <button
                          type="button"
                          className="btn btn-setlist-link-color"
                          style={{ alignSelf: 'flex-start' }}
                          onClick={() =>
                            setExpandedNoteIds((prev) =>
                              prev.includes(note.id)
                                ? prev
                                : [...prev, note.id],
                            )
                          }
                        >
                          See more
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
            </>
          )}

          {showAttachments && (
            <>
              {attachmentsLoading && (
                <div className="d-flex align-items-center justify-content-center text-muted small h-100">
                  Loading attachments...
                </div>
              )}
              {!attachmentsLoading && attachmentsError && (
                <div className="text-danger small py-2">{attachmentsError}</div>
              )}
              {!attachmentsLoading && !attachmentsError && attachments.length === 0 && (
                <div className="d-flex align-items-center justify-content-center text-muted small py-4">
                  No attachments yet.
                </div>
              )}
              {!attachmentsLoading && !attachmentsError && attachments.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {attachments.map((att) => (
                    <div
                      key={att.id}
                      className="d-flex align-items-center"
                      style={{ gap: 12 }}
                    >
                      <i
                        className="fal fa-paperclip"
                        style={{ fontSize: 18, color: 'var(--sl-text-quaternary)' }}
                        aria-hidden="true"
                      />
                      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                        <a
                          href={att.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate"
                          style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-secondary)' }}
                        >
                          {att.file_name}
                        </a>
                        <span style={{ fontSize: 12, color: 'var(--sl-text-quaternary)' }}>
                          {[att.content_type, att.human_size, att.human_created_at]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </div>
                      {canManageAttachments && !readOnly && onDeleteAttachment && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-muted"
                          aria-label="Delete attachment"
                          onClick={() => onDeleteAttachment(att)}
                        >
                          <i className="fal fa-trash-alt" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {!readOnly && (
          <div
            style={{
              borderTop: '1px solid var(--sl-border-secondary)',
              padding: '12px 24px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <textarea
              rows={2}
              placeholder="Add a note…"
              value={noteBody}
              onChange={(e) => {
                setNoteBody(e.target.value);
                setComposerError('');
              }}
              disabled={noteSubmitting}
              style={{
                width: '100%',
                border: '1px solid var(--sl-border-secondary)',
                borderRadius: 8,
                backgroundColor: 'var(--sl-bg-primary)',
                color: 'var(--sl-text-primary)',
                fontSize: 14,
                lineHeight: '20px',
                padding: '8px 12px',
                resize: 'vertical',
              }}
            />
            {composerError && (
              <p className="mb-0" style={{ fontSize: 12, color: 'var(--sl-text-error)' }}>
                {composerError}
              </p>
            )}
            <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
              {canManageAttachments && onUploadAttachment && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <button
                    type="button"
                    className="btn btn-setlist-secondary d-inline-flex align-items-center justify-content-center"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    aria-label="Add attachment"
                    style={{ width: 36, height: 36, padding: 8 }}
                  >
                    <i className="fal fa-paperclip" style={{ fontSize: 20 }} aria-hidden="true" />
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-setlist-primary"
                onClick={handleCreateNote}
                disabled={!noteBody.trim() || noteSubmitting || !onCreateNote}
                aria-label="Add note"
              >
                {noteSubmitting ? 'Adding…' : 'Add note'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CrmNotesCard;
